import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '@et/environment';

export interface TwoFactorEnableResult {
  totpURI?: string;
  backupCodes?: string[];
}

/**
 * Shared Better Auth `twoFactor` plugin HTTP calls, used by both
 * `TwoFactorSettingsComponent` (self-service, `/app/profile/settings`) and
 * `TwoFactorRequiredComponent` (mandatory blocking enrollment gate,
 * `/auth/two-factor-required`) so the HTTP wiring only lives in one place. All
 * calls carry the Better Auth session cookie (`withCredentials`);
 * `enable`/`disable`/`regenerateBackupCodes` require password re-entry per the
 * plugin contract.
 */
@Injectable({ providedIn: 'root' })
export class TwoFactorEnrollmentService {
  private http = inject(HttpClient);
  private base = environment.betterAuth?.baseUrl ?? '';

  /**
   * Starts enrollment. Only ever returns `{ totpURI, backupCodes }` — it never
   * flips `user.twoFactorEnabled` (server has `skipVerificationOnEnable` removed).
   * A real `verifyTotp`/`verifyOtp` round trip is required next to activate.
   */
  enable(password: string): Promise<TwoFactorEnableResult> {
    return firstValueFrom(
      this.http.post<TwoFactorEnableResult>(
        `${this.base}/api/auth/two-factor/enable`,
        { password },
        { withCredentials: true },
      ),
    );
  }

  /** Completes activation with a live authenticator-app TOTP code. */
  verifyTotp(code: string): Promise<unknown> {
    return firstValueFrom(
      this.http.post(
        `${this.base}/api/auth/two-factor/verify-totp`,
        { code },
        { withCredentials: true },
      ),
    );
  }

  /** Completes activation with the emailed/SMS OTP code. */
  verifyOtp(code: string): Promise<unknown> {
    return firstValueFrom(
      this.http.post(
        `${this.base}/api/auth/two-factor/verify-otp`,
        { code },
        { withCredentials: true },
      ),
    );
  }

  /** Requests the emailed/SMS OTP to confirm with (`activationMethod === 'otp'`). */
  sendOtp(): Promise<unknown> {
    return firstValueFrom(
      this.http.post(
        `${this.base}/api/auth/two-factor/send-otp`,
        {},
        { withCredentials: true },
      ),
    );
  }

  disable(password: string): Promise<unknown> {
    return firstValueFrom(
      this.http.post(
        `${this.base}/api/auth/two-factor/disable`,
        { password },
        { withCredentials: true },
      ),
    );
  }

  regenerateBackupCodes(password: string): Promise<TwoFactorEnableResult> {
    return firstValueFrom(
      this.http.post<TwoFactorEnableResult>(
        `${this.base}/api/auth/two-factor/generate-backup-codes`,
        { password },
        { withCredentials: true },
      ),
    );
  }
}
